/**
 * GameTypeSelector.js
 * Visual game type selector with cards
 */

import React from 'react';
import {
    Grid,
    Card,
    CardContent,
    CardActions,
    Typography,
    Button,
    Box,
    Chip,
    CircularProgress
} from '@mui/material';
import {
    People as PeopleIcon,
    Timer as TimerIcon,
    TouchApp as TouchAppIcon
} from '@mui/icons-material';

export default function GameTypeSelector({ gameTypes, onSelect, loading }) {
    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            <Typography variant="h5" gutterBottom>
                Choose Your Game Type
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
                Select the type of game you want to create. Each game type has unique rules and configurations.
            </Typography>

            <Grid container spacing={3} sx={{ mt: 2 }}>
                {gameTypes.map((gameType) => (
                    <Grid item xs={12} md={6} lg={4} key={gameType.id}>
                        <Card
                            elevation={3}
                            sx={{
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                transition: 'transform 0.2s',
                                '&:hover': {
                                    transform: 'translateY(-4px)',
                                    boxShadow: 6
                                }
                            }}
                        >
                            <CardContent sx={{ flexGrow: 1 }}>
                                <Typography variant="h6" component="div" gutterBottom>
                                    {gameType.name}
                                </Typography>

                                <Typography variant="body2" color="text.secondary" paragraph>
                                    {gameType.description}
                                </Typography>

                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
                                    <Chip
                                        icon={<PeopleIcon />}
                                        label={`${gameType.minPlayers}-${gameType.maxPlayers} players`}
                                        size="small"
                                        variant="outlined"
                                    />
                                    <Chip
                                        icon={<TimerIcon />}
                                        label={gameType.estimatedDuration}
                                        size="small"
                                        variant="outlined"
                                    />
                                    {gameType.supportsHumanPlayers && (
                                        <Chip
                                            icon={<TouchAppIcon />}
                                            label="Human players"
                                            size="small"
                                            color="primary"
                                            variant="outlined"
                                        />
                                    )}
                                </Box>

                                <Box sx={{ mt: 2 }}>
                                    <Typography variant="caption" color="text.secondary">
                                        Recommended: {gameType.recommendedPlayers} players
                                    </Typography>
                                </Box>
                            </CardContent>

                            <CardActions>
                                <Button
                                    size="large"
                                    variant="contained"
                                    fullWidth
                                    onClick={() => onSelect(gameType)}
                                >
                                    Select
                                </Button>
                            </CardActions>
                        </Card>
                    </Grid>
                ))}
            </Grid>
        </Box>
    );
}
